import {
  AccountProductEnum,
  AdvanceFeeTier,
  Query,
  TransferOptionsResultTypenames,
} from '@varo/graphql-schema-types';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { EXTERNAL_DEBIT_ACCOUNT_TYPE } from 'features/feeBasedInstantTransfer/helpers/transferPreselection';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { requestContentCardsRefresh } from 'services/engagement/braze/braze';
import FeeBasedTransferExternalDebitCardsQuery from 'services/graphql/queries/MoveMoney/FeeBasedTransferExternalDebitCardsQuery';
import { ExperimentKey, FeatureFlagKey } from 'services/statsig/types';
import { TRANSFER_TYPES } from 'shared/analytics/events/transferMoney';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import useIsFeatureEnabled from 'shared/hooks/statsig/useIsFeatureEnabled/useIsFeatureEnabled';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { useEngagement } from 'shared/hooks/useBraze';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

const INSTANT_TRANSFER_MINIMUM_ADVANCE_AMOUNT = 20;

const GetAdvanceSuccessScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'getAdvanceSuccessStrings',
  });
  const isEncourageDdAfterVaSuccessEnabled = useIsFeatureEnabled(
    FeatureFlagKey.ENCOURAGE_DD_AFTER_VA_SUCCESS,
  );
  const { experimentSegmentActive, isFeatureEnabled } = useStatsig();

  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.ADVANCE_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU,
    HomeTileTypeIds.NEXT_BEST_ACTION_TILE,
  ]);
  const { logCustomEvent, setRequestedCardId, requestedCard } = useEngagement();

  useEffect(() => {
    if (isEncourageDdAfterVaSuccessEnabled) {
      setRequestedCardId?.(null);
      return;
    }

    logCustomEvent('BRAZETRIGGER, get advance success screen viewed');

    requestContentCardsRefresh();
    setRequestedCardId?.('get_advance_success_screen_offer');
  }, [isEncourageDdAfterVaSuccessEnabled, logCustomEvent, setRequestedCardId]);

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  const {
    advanceAmount,
    feeTier: rawFeeTier,
    formattedAdvanceAmount,
    message,
  } = useLocalSearchParams<{
    advanceAmount?: string;
    feeTier?: AdvanceFeeTier;
    formattedAdvanceAmount?: string;
    message?: string;
  }>();

  const isInstantTransferEntryEligible =
    Number(advanceAmount) > INSTANT_TRANSFER_MINIMUM_ADVANCE_AMOUNT &&
    isFeatureEnabled(FeatureFlagKey.FBIT_PILOT);

  const [
    { data: externalDebitCardsData, fetching: isFetchingExternalDebitCards },
  ] = useQuery<Query>({
    query: FeeBasedTransferExternalDebitCardsQuery,
    pause: !isInstantTransferEntryEligible,
    requestPolicy: 'network-only',
  });

  const hasSelectableExternalDebitCard =
    externalDebitCardsData?.transferOptions.__typename ===
      TransferOptionsResultTypenames.TransferOptions &&
    externalDebitCardsData.transferOptions.destinationExternalDebitCards.some(
      (externalDebitCard) => externalDebitCard.isSelectable,
    );

  // The experiment is checked last so only customers who can see the entry
  // point are exposed to it.
  const isInstantTransferEntryEnabled =
    isInstantTransferEntryEligible &&
    hasSelectableExternalDebitCard &&
    experimentSegmentActive(ExperimentKey.FBIT_VA_ENTRY_POINT, 'Test');

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    if (isEncourageDdAfterVaSuccessEnabled) {
      router.dismissAll();

      if (rawFeeTier === AdvanceFeeTier.HigherFee) {
        router.navigate({
          pathname: '/',
          params: {
            encourageDdAfterVaSuccess: 'true',
            feeTier: rawFeeTier,
          },
        });
        return;
      }

      router.navigate('/');
      return;
    }

    dismissToTab(Tabs.HOME);
  };

  const contentText = isEncourageDdAfterVaSuccessEnabled
    ? translatedText('getAdvanceSuccessfulNewMessage', {
        advanceAmount: formattedAdvanceAmount,
      })
    : message;

  useBackHandler(goHome);

  // Finish this screen the same way Done does so going back from instant
  // transfer lands where Done would, without returning to this screen.
  const handleTransferInstantly = () => {
    goHome();
    router.push({
      pathname: '/move-money/transfer/instant-transfer',
      params: {
        fromType: AccountProductEnum.Checking,
        toType: EXTERNAL_DEBIT_ACCOUNT_TYPE,
        targetAmount: advanceAmount,
        disallowAccountInteraction: 'true',
      },
    });
  };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: false,
          gestureEnabled: false,
        }}
      />
      <ScreenTransitionOverlay
        isLoading={isFetchingExternalDebitCards}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={null}
      />
      <SuccessScreen
        primaryButton={{
          label: translatedText('doneButton'),
          onPress: goHome,
          analyticsId: 'gotoHomeClicked',
          customAttributes: { cta: 'Done' },
        }}
        secondaryButton={
          isInstantTransferEntryEnabled
            ? {
                label: translatedText('transferInstantlyButton'),
                onPress: handleTransferInstantly,
                analyticsId: 'transferInstantly',
                customAttributes: {
                  name: 'va_executed',
                  transfer_type: TRANSFER_TYPES.PUSH_TO_CARD,
                },
              }
            : undefined
        }
        eyebrowText={
          isEncourageDdAfterVaSuccessEnabled
            ? translatedText('getAdvanceSuccessfulEyebrow')
            : undefined
        }
        visual="CASH_B_ANIMATED"
        titleText={translatedText(
          isEncourageDdAfterVaSuccessEnabled
            ? 'getAdvanceSuccessfulNewLabel'
            : 'getAdvanceSuccessfulLabel',
        )}
        content={
          <BodyBaseText numberOfLines={0} style={styles.contentText}>
            {contentText}
          </BodyBaseText>
        }
        productOfferCard={
          isEncourageDdAfterVaSuccessEnabled ? undefined : requestedCard
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentText: {
    textAlign: 'center',
  },
});

export default GetAdvanceSuccessScreen;
